/**
 * Dados geográficos usados pelo componente Geography do EP.
 *
 * Não é Service, Domain ou Module. O componente consome diretamente o dataset
 * público versionado e mantém cache local em memória no browser.
 */

export const GEOGRAPHY_DATASET = Object.freeze({
  package: '@countrystatecity/countries-browser',
  version: '1.0.4',
  license: 'ODbL-1.0',
  attribution: 'Countries States Cities Database (dr5hn)',
});

const BASE_URL = `https://cdn.jsdelivr.net/npm/${GEOGRAPHY_DATASET.package}@${GEOGRAPHY_DATASET.version}/dist/data`;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MIN_COUNTRIES = 240;

const CENTRAL_AMERICA_CODES = new Set([
  'BZ', 'CR', 'SV', 'GT', 'HN', 'NI', 'PA',
]);

const SOUTH_AMERICA_CODES = new Set([
  'AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'FK', 'GF', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE',
]);

const cache = new Map();

const cloneRows = (rows) => Array.isArray(rows)
  ? rows.map((row) => ({
      ...row,
      ...(row?.group && typeof row.group === 'object'
        ? { group: { ...row.group } }
        : {}),
    }))
  : [];

const cached = (key, allowExpired = false) => {
  const item = cache.get(key);
  if (!item || !Array.isArray(item.rows)) return null;
  if (!allowExpired && Date.now() >= item.expiresAt) return null;
  return cloneRows(item.rows);
};

const store = (key, rows) => {
  cache.set(key, {
    rows: cloneRows(rows),
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
};

const normalizeCountry = (value) => {
  const code = String(value || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : '';
};

const normalizeState = (value) => {
  const code = String(value || '').trim().toUpperCase();
  return code && code.length <= 16 ? code : '';
};

const firstValue = (source = {}, keys = []) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
};

const browserLocale = () => {
  const explicit = String(globalThis?.dic?.language || '').trim();
  if (explicit) return explicit;

  const language = String(globalThis?.navigator?.language || '').trim();
  return language || 'pt-BR';
};

const displayNames = () => {
  try {
    return new Intl.DisplayNames([browserLocale()], { type: 'region' });
  } catch {
    return null;
  }
};

/**
 * Resolve o nome do país no locale corrente (`Intl.DisplayNames`, nativo do
 * runtime), com fallback explícito para quando o locale não traduzir o
 * código. Outros componentes do EP que precisam exibir nome de país (ex.:
 * Phone) devem reutilizar esta função em vez de manter uma lista própria de
 * nomes traduzidos.
 *
 * @param {string} iso2 Código ISO 3166-1 alpha-2.
 * @param {string} [fallback=''] Nome a usar quando o locale não resolver.
 * @returns {string} Nome do país no locale corrente, ou o fallback.
 */
export const countryDisplayName = (iso2, fallback = '') => {
  const code = String(iso2 || '').trim().toUpperCase();
  // "ZZ" é reservado pelo CLDR/Unicode para "região desconhecida"; o runtime
  // devolve essa frase (traduzida) em vez do próprio código, então o check
  // abaixo de "veio igual ao código" não pega esse caso específico.
  const localized = code && code !== 'ZZ' ? displayNames()?.of(code) : null;
  return String(localized && localized !== code ? localized : fallback || '').trim();
};

const continentKey = (country = {}) => {
  const region = String(country.region || '').trim();
  const iso2 = normalizeCountry(country.iso2);

  if (region === 'Americas') {
    if (CENTRAL_AMERICA_CODES.has(iso2)) return 'centralAmerica';
    if (SOUTH_AMERICA_CODES.has(iso2)) return 'southAmerica';
    return 'northAmerica';
  }

  const keys = {
    Africa: 'africa',
    Asia: 'asia',
    Europe: 'europe',
    Oceania: 'oceania',
  };

  return keys[region] || '';
};

const continentGroup = (country) => {
  const key = continentKey(country);
  const label = globalThis?.dic?.labels?.continents?.[key];
  if (!key || !label) return null;

  return {
    key,
    label,
    text: label,
  };
};

const fetchRows = async (resource, fetcher = globalThis?.fetch?.bind(globalThis)) => {
  if (typeof fetcher !== 'function') {
    throw new Error('Geography: fetch indisponível.');
  }

  const response = await fetcher(`${BASE_URL}/${resource}`, {
    headers: {
      Accept: 'application/json',
    },
  });

  if (response.status === 404) return [];
  if (!response.ok) {
    throw new Error(`Geography: dataset respondeu HTTP ${response.status}.`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

const countryRows = async () => {
  const key = 'countries';
  const current = cached(key);
  if (current) return current;

  try {
    const rows = await fetchRows('countries.json');
    const byIso = new Map();

    for (const row of rows) {
      const iso2 = normalizeCountry(
        row?.iso2
        ?? row?.Iso2
        ?? row?.ISO2
        ?? row?.country_code
        ?? row?.countryCode
      );
      const name = String(row?.name ?? row?.country ?? '').trim();
      const iso3 = String(row?.iso3 ?? row?.Iso3 ?? row?.ISO3 ?? '').trim().toUpperCase();
      const region = String(row?.region ?? row?.continent ?? '').trim();

      if (!iso2 || !name || byIso.has(iso2)) continue;
      byIso.set(iso2, {
        name,
        iso2,
        ...(iso3 ? { iso3 } : {}),
        ...(region ? { region } : {}),
      });
    }

    const countries = [...byIso.values()];
    if (countries.length < MIN_COUNTRIES) {
      throw new Error(`Geography: dataset incompleto (${countries.length} países).`);
    }

    store(key, countries);
    return cloneRows(countries);
  } catch (error) {
    const stale = cached(key, true);
    if (stale) return stale;
    throw error;
  }
};

const stateRows = async (country) => {
  const countryCode = normalizeCountry(country);
  if (!countryCode) return [];

  const key = `states:${countryCode}`;
  const current = cached(key);
  if (current) return current;

  try {
    const rows = await fetchRows(`states/${encodeURIComponent(countryCode)}.json`);
    const byCode = new Map();

    for (const row of rows) {
      const code = normalizeState(
        row?.iso2
        ?? row?.state_code
        ?? row?.stateCode
        ?? row?.code
      );
      const name = String(row?.name || '').trim();
      if (!code || !name || byCode.has(code)) continue;

      byCode.set(code, {
        name,
        code,
        countryCode,
      });
    }

    const states = [...byCode.values()];
    store(key, states);
    return cloneRows(states);
  } catch (error) {
    const stale = cached(key, true);
    if (stale) return stale;
    throw error;
  }
};

const cityRows = async (country, state) => {
  const countryCode = normalizeCountry(country);
  const stateCode = normalizeState(state);
  if (!countryCode || !stateCode) return [];

  const key = `cities:${countryCode}:${stateCode}`;
  const current = cached(key);
  if (current) return current;

  try {
    const rows = await fetchRows(
      `cities/${encodeURIComponent(countryCode)}-${encodeURIComponent(stateCode)}.json`
    );
    const byId = new Map();

    for (const row of rows) {
      const id = String(row?.id ?? row?.city_id ?? row?.cityId ?? '').trim();
      const name = String(row?.name || '').trim();
      if (!id || !name || byId.has(id)) continue;

      byId.set(id, {
        id,
        name,
        countryCode,
        stateCode,
      });
    }

    const cities = [...byId.values()];
    store(key, cities);
    return cloneRows(cities);
  } catch (error) {
    const stale = cached(key, true);
    if (stale) return stale;
    throw error;
  }
};

const sortByLabel = (rows) => rows.sort((left, right) => (
  String(left?.label || '').localeCompare(
    String(right?.label || ''),
    browserLocale(),
    { sensitivity: 'base', numeric: true }
  )
));

export const getGeographyOptions = async (level, options = {}) => {
  const values = options?.params && typeof options.params === 'object'
    ? options.params
    : (
      options?.values && typeof options.values === 'object'
        ? options.values
        : {}
    );

  if (level === 'country') {
    const rows = await countryRows();

    return rows
      .map((country) => {
        const label = countryDisplayName(country.iso2, country.name);
        const group = continentGroup(country);

        return {
          label,
          text: label,
          value: country.iso2,
          iso2: country.iso2,
          ...(country.iso3 ? { iso3: country.iso3 } : {}),
          icon: {
            type: 'ep',
            name: `flags ${country.iso2.toLowerCase()}`,
          },
          ...(group ? { group } : {}),
        };
      })
      .sort((left, right) => {
        const groupCompare = String(left?.group?.label || '').localeCompare(
          String(right?.group?.label || ''),
          browserLocale(),
          { sensitivity: 'base' }
        );
        return groupCompare || String(left.label).localeCompare(
          String(right.label),
          browserLocale(),
          { sensitivity: 'base' }
        );
      });
  }

  const country = firstValue(values, [
    'country',
    'countryCode',
    'country_code',
  ]);

  if (level === 'state') {
    if (!country) return [];

    return sortByLabel(
      (await stateRows(country)).map((state) => ({
        label: state.name,
        text: state.name,
        value: state.code,
        code: state.code,
        countryCode: state.countryCode,
      }))
    );
  }

  const state = firstValue(values, [
    'state',
    'stateCode',
    'state_code',
    'uf',
  ]);

  if (level === 'city') {
    if (!country || !state) return [];

    return sortByLabel(
      (await cityRows(country, state)).map((city) => ({
        label: city.name,
        text: city.name,
        value: city.id,
        id: city.id,
        countryCode: city.countryCode,
        stateCode: city.stateCode,
      }))
    );
  }

  return [];
};

export const clearGeographyCache = () => cache.clear();
